/**
 * BulkAssignProvidersPanel — 80%-width right drawer to bulk ASSIGN or UNASSIGN
 * providers to/from many selected clients. Lists org-wide providers (searchable
 * by name/location), multi-select, then applies the action to every
 * (selected client × selected provider) pair via the bulk-multi endpoint.
 */

import { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Check, Search, MapPin } from 'lucide-react';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { media } from '../../styles/media';
import { useAllAssignableProviders, useBulkUpdateClientProviders } from '../../hooks/useClients';
import type {
  AssignableProviderLite,
  BulkUpdateClientProvidersResult,
} from '../../services/api/clientService';
import { formatPersonMeta, matchesPerson } from '../shared/PersonMeta';

export type BulkMode = 'assign' | 'unassign';

interface BulkAssignProvidersPanelProps {
  clientIds: string[];
  mode: BulkMode;
  onClose: () => void;
  /** Called after a successful apply (parent clears selection + closes). */
  onDone: () => void;
}

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(80vw, 1280px);
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} ${({ theme }) => theme.transitions.base} ease-out;

  ${media.desktop} {
    width: 90vw;
  }
  ${media.tablet} {
    width: 100vw;
  }
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SearchBox = styled.div`
  position: relative;
  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.light};
  }
  input {
    width: 100%;
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
    padding-left: 34px;
    border: 1px solid ${({ theme }) => theme.colors.borders.medium};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing[2]};

  ${media.tablet} {
    grid-template-columns: 1fr;
  }
`;

const Row = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary.base : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $selected }) =>
    $selected
      ? (theme.colors.primary.lightSky ?? theme.colors.backgrounds.light)
      : theme.colors.backgrounds.white};
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const CheckBox = styled.span<{ $checked?: boolean }>`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1.5px solid
    ${({ theme, $checked }) => ($checked ? theme.colors.primary.base : theme.colors.borders.medium)};
  background: ${({ theme, $checked }) => ($checked ? theme.colors.primary.base : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  svg {
    width: 12px;
    height: 12px;
  }
`;

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  .meta {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.text.secondary};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    /* "Location · Role" can be long — wrap instead of widening the row. */
    overflow-wrap: anywhere;
    svg {
      flex-shrink: 0;
      width: 11px;
      height: 11px;
    }
  }
`;

const StateBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const EmptyNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

const ResultBox = styled.div`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const fullName = (p: AssignableProviderLite) => `${p.firstName} ${p.lastName}`.trim();

export function BulkAssignProvidersPanel({
  clientIds,
  mode,
  onClose,
  onDone,
}: BulkAssignProvidersPanelProps) {
  const { data, isLoading, isError } = useAllAssignableProviders(true);
  const mutation = useBulkUpdateClientProviders();

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkUpdateClientProvidersResult | null>(null);

  const isAssign = mode === 'assign';
  const providers = useMemo(() => data ?? [], [data]);

  // Matches name, email, location/center and role (ADO 398 + 406).
  const filtered = useMemo(
    () => providers.filter((p) => matchesPerson(p, search)),
    [providers, search],
  );

  const toggle = (memberId: string) =>
    setSelectedIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );

  const handleApply = () => {
    if (selectedIds.length === 0) return;
    setError(null);
    mutation.mutate(
      { clientIds, providerMemberIds: selectedIds, action: isAssign ? 'ASSIGN' : 'UNASSIGN' },
      {
        onSuccess: (res) => setResult(res),
        onError: () => setError('Bulk update failed. Please try again.'),
      },
    );
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <Header>
          <div>
            <Title>{isAssign ? 'Assign providers' : 'Unassign providers'}</Title>
            <Subtitle>
              {isAssign ? 'to' : 'from'} {clientIds.length} client
              {clientIds.length === 1 ? '' : 's'}
            </Subtitle>
          </div>
          <CloseBtn onClick={onClose} aria-label="Close">
            <X />
          </CloseBtn>
        </Header>

        <Body>
          {result ? (
            <ResultBox>
              {isAssign ? 'Assigned' : 'Unassigned'} {result.affected} link
              {result.affected === 1 ? '' : 's'}
              {result.skipped > 0 ? ` · ${result.skipped} skipped (no change needed)` : ''}.
            </ResultBox>
          ) : (
            <>
              <SearchBox>
                <Search />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search providers by name, location, or role..."
                />
              </SearchBox>

              {isLoading && (
                <StateBox>
                  <LoadingSpinner size="sm" /> Loading providers...
                </StateBox>
              )}
              {isError && !isLoading && <ErrorText>Failed to load providers.</ErrorText>}
              {!isLoading && !isError && filtered.length === 0 && (
                <EmptyNote>No matching providers.</EmptyNote>
              )}

              <List>
                {filtered.map((p) => {
                  const checked = selectedIds.includes(p.memberId);
                  return (
                    <Row
                      key={p.memberId}
                      type="button"
                      $selected={checked}
                      onClick={() => toggle(p.memberId)}
                    >
                      <CheckBox $checked={checked}>{checked && <Check />}</CheckBox>
                      <RowInfo>
                        <div className="name">{fullName(p)}</div>
                        <div className="meta">
                          <MapPin />{' '}
                          {formatPersonMeta({
                            centerName: p.centerName,
                            roleName: p.roleName,
                            locationFallback: 'No location',
                          })}
                        </div>
                      </RowInfo>
                    </Row>
                  );
                })}
              </List>

              {error && <ErrorText>{error}</ErrorText>}
            </>
          )}
        </Body>

        <Footer>
          {result ? (
            <Button variant="primary" onClick={onDone}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant={isAssign ? 'primary' : 'danger'}
                disabled={selectedIds.length === 0}
                isLoading={mutation.isPending}
                onClick={handleApply}
              >
                {isAssign ? 'Assign' : 'Unassign'}
                {selectedIds.length > 0 ? ` ${selectedIds.length}` : ''}
              </Button>
            </>
          )}
        </Footer>
      </Panel>
    </Overlay>
  );
}

export default BulkAssignProvidersPanel;
